import {
  Form,
  Icon,
  ICONS,
  Button,
  TextInput,
  Typography,
  SelectInput,
  DatePickerInput,
  TYPOGRAPHY_SIZE,
  PLATFORM_THEME_COLORS,
  HoursInput,
} from '@thinknet-gmbh/platform-common-ui';
import { useTranslation } from 'react-i18next';

import { useGetGantts } from 'src/hooks/gantts/get-gantts.hook';
import { useDeleteTimeStripe } from 'src/hooks/time-stripes/delete-time-stripe.hook';

import { ITimeStripesListProps } from './time-stripes-list.interfaces';
import { AddWrapper, TimeStripesGridWrapper, DeleteWrapper } from './time-stripes-list.styles';

const TimeStripesList = ({ field, isDisabled, form, selectedGantts }: ITimeStripesListProps) => {
  const { t, i18n } = useTranslation(['common', 'projects']);
  const { tasks } = form.getFieldsValue(true);
  const { gantts } = useGetGantts();
  const deleteTimeStripe = useDeleteTimeStripe();

  return (
    <Form.List name={[field.name, 'timeStripes']}>
      {(stripeFields, { add: addTimeStripe, remove: removeTimeStripe }) => {
        const onDeleteTimeStripe = (stripeField: any) => {
          const targetTimeStripeId = tasks[field.name].timeStripes[stripeField.name]?._id || null;

          if (targetTimeStripeId) {
            deleteTimeStripe({ variables: { timeStripeId: targetTimeStripeId } });
          }

          removeTimeStripe(stripeField.name);
        };

        return (
          <div>
            <Typography
              weight="bold"
              fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}
              size={TYPOGRAPHY_SIZE.small}
            >
              {t('projects:labels.timeStripe')}
            </Typography>
            <br />
            <TimeStripesGridWrapper>
              {stripeFields.map((stripeField, fieldIndex) => (
                <div key={stripeField.key}>
                  <Form.Item
                    name={[stripeField.name, 'name']}
                    label={t('projects:task.timeStripeName')}
                    rules={[
                      {
                        required: true,
                        message: t('projects:validation.timeStripeNameRequired'),
                      },
                    ]}
                  >
                    <TextInput placeholder={t('projects:task.timeStripeName')} />
                  </Form.Item>

                  <Form.Item
                    name={[stripeField.name, 'plannedHours']}
                    label={t('projects:labels.plannedHours')}
                    rules={[
                      {
                        required: true,
                        message: t('projects:validation.plannedHoursRequired'),
                      },
                    ]}
                  >
                    <HoursInput
                      maxValue={10000}
                      placeholder="10 hr"
                      disabled={
                        isDisabled &&
                        !!tasks[field.key]?.timeStripes[stripeField.key]?._id &&
                        tasks[field.key]?.timeStripes[stripeField.key].startDate.isValid()
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    name={[stripeField.name, 'ganttId']}
                    label={t('common:labels.gantt')}
                    rules={[
                      { required: true, message: t('common:validation.error.ganttRequired') },
                      () => ({
                        validator(_, value) {
                          if (selectedGantts.includes(value)) {
                            return Promise.resolve();
                          }

                          return Promise.reject(new Error(t('projects:validation.incorrectGantt')));
                        },
                      }),
                    ]}
                  >
                    <SelectInput placeholder={t('projects:task.selectNeededGantt')}>
                      {gantts
                        .filter((gantt: any) => selectedGantts.includes(gantt._id))
                        .map((gantt: any) => (
                          <SelectInput.Option key={gantt._id} value={gantt._id}>
                            {gantt.name}
                          </SelectInput.Option>
                        ))}
                    </SelectInput>
                  </Form.Item>

                  <Form.Item shouldUpdate noStyle>
                    {({ getFieldValue }) => {
                      const timeStripes = getFieldValue(['tasks', field.name, 'timeStripes']);
                      const timeStripe = timeStripes[stripeField.key];
                      const isValid = timeStripe?.startDate && timeStripe.startDate.isValid();

                      return (
                        <Form.Item hidden={!timeStripe?._id} label={t('projects:labels.startDate')}>
                          <DatePickerInput
                            lang={i18n.language}
                            disabled
                            format="DD.MM.YYYY"
                            {...(isValid
                              ? { value: timeStripe?.startDate }
                              : { placeholder: t('projects:labels.timeStripeNotAssigned') })}
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>

                  {stripeFields.length > 1 && (
                    <DeleteWrapper>
                      <Icon
                        icon={ICONS.trash}
                        color={PLATFORM_THEME_COLORS.tone.red}
                        size={22}
                        rightMargin={false}
                        onClick={() => onDeleteTimeStripe(stripeField)}
                      />
                    </DeleteWrapper>
                  )}
                  {stripeFields.length === fieldIndex + 1 && (
                    <AddWrapper>
                      <Button
                        type="text"
                        icon={ICONS.plus}
                        iconColor={PLATFORM_THEME_COLORS.primary.blue}
                        onClick={() => {
                          const initialTimeStripe: any = { name: stripeField.name + 2 };
                          const projectName = form.getFieldValue('name');
                          const taskName = form.getFieldValue(['tasks', field.name, 'name']);
                          const ganttIds = form.getFieldValue('ganttIds') || [];

                          if (ganttIds.length === 1) {
                            initialTimeStripe.ganttId = ganttIds[0];
                          }

                          if (projectName || taskName) {
                            initialTimeStripe.name = `${stripeFields.length + 1}`;
                            initialTimeStripe.createdAt = new Date();
                          }

                          addTimeStripe(initialTimeStripe);
                        }}
                      >
                        {t('projects:buttons.addTimeStripe')}
                      </Button>
                    </AddWrapper>
                  )}
                </div>
              ))}
            </TimeStripesGridWrapper>
          </div>
        );
      }}
    </Form.List>
  );
};

export default TimeStripesList;
